// Send to Planner: choose the workbook, check every card in a preview, confirm once in Windows, done.
import { useMemo, useRef, useState } from "react";
import { T, Tf } from "../lib/i18n";
import { Icon, Orb, Pill, useStudio } from "./ui";
import { cleanError, graph, useGraph, type Plan } from "../lib/m365";
import { matchOwner, parseWorkbook, PROGRESS_LABEL, type Member, type ParsedSheet } from "../lib/plannerImport";

type Prep = { buckets: { id: string; name: string }[]; titles: string[]; labels: Record<string, string>; members: Member[] };
type BucketMode = "source" | "milestone" | "single";

function labelSlots(existing: Record<string, string>) {
  const want = ["At Risk", "Blocked"] as const;
  const map: Record<string, string> = {}; const names: Record<string, string> = {};
  for (const w of want) {
    const found = Object.entries(existing).find(([, v]) => v?.toLowerCase() === w.toLowerCase());
    if (found) { map[w] = found[0]; continue; }
    for (let i = 1; i <= 25; i++) { const k = `category${i}`; if (!existing[k] && !Object.keys(names).includes(k)) { map[w] = k; names[k] = w; break; } }
  }
  return { map, names };
}

export function SendToPlanner({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { toast } = useStudio();
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [sheets, setSheets] = useState<ParsedSheet[] | null>(null);
  const [sheetIdx, setSheetIdx] = useState(0);
  const { data: plans, error: plansError, reload: reloadPlans } = useGraph<(Plan & { groupId?: string })[]>("planner.plans", true);
  const [newPlan, setNewPlan] = useState(false);
  const [groups, setGroups] = useState<{ id: string; name: string }[] | null>(null);
  const [groupId, setGroupId] = useState("");
  const [planName, setPlanName] = useState("VERA Operating");
  const [created, setCreated] = useState<(Plan & { groupId?: string }) | null>(null);
  const [planId, setPlanId] = useState("");
  const [prep, setPrep] = useState<Prep | null>(null);
  const [mode, setMode] = useState<BucketMode>("source");
  const [skip, setSkip] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState<{ ok: number; failed: { key: string; error?: string }[] } | null>(null);

  const plan = plans?.find((p) => p.id === planId) ?? (created?.id === planId ? created : undefined);
  const sheet = sheets?.[sheetIdx];

  async function pickFile(f: File) {
    setError(""); setBusy("read"); setFileName(f.name);
    try {
      const parsed = await parseWorkbook(await f.arrayBuffer());
      if (!parsed.length) setError(T("This workbook has no Action Log or Milestones sheet that VERA recognises."));
      setSheets(parsed); setSheetIdx(0); setSkip(new Set());
    } catch { setError(T("VERA couldn't read that file. Please choose an Excel workbook (.xlsx).")); }
    finally { setBusy(""); }
  }
  async function pickPlan(id: string, known?: Plan & { groupId?: string }) {
    if (id === "__new") { setNewPlan(true); setPlanId(""); setPrep(null); if (!groups) graph<{ id: string; name: string }[]>("planner.groups").then((g) => { setGroups(g); setGroupId(g.find((x) => /veraneural/i.test(x.name))?.id ?? g[0]?.id ?? ""); }).catch((e) => setError(cleanError(e))); return; }
    setNewPlan(false); setPlanId(id); setPrep(null); setError("");
    const p = known ?? plans?.find((x) => x.id === id);
    if (!p) return;
    setBusy("plan");
    try { setPrep(await graph<Prep>("planner.prepare", { planId: p.id, groupId: p.groupId })); }
    catch (e) { setError(cleanError(e)); } finally { setBusy(""); }
  }
  async function createPlan() {
    const g = groups?.find((x) => x.id === groupId);
    if (!g || !planName.trim()) return;
    setBusy("create"); setError("");
    try {
      const r = await graph<{ cancelled?: boolean; id: string; title: string; group: string; groupId: string }>("planner.createPlan", { groupId: g.id, groupName: g.name, title: planName.trim() });
      if (r.cancelled) { setBusy(""); return; }
      toast(Tf("Plan \"{title}\" created in {group}.", { title: r.title, group: r.group }));
      await reloadPlans();
      setCreated(r);
      setBusy("");
      await pickPlan(r.id, r);
    } catch (e) { setError(cleanError(e)); setBusy(""); }
  }


  const rows = useMemo(() => {
    if (!sheet) return [];
    return sheet.cards.map((c) => {
      const already = !!prep?.titles.some((t) => t.startsWith(`${c.key} · `));
      const owner = prep ? matchOwner(c.owner, prep.members) : null;
      const bucket = mode === "single" ? `Imported: ${sheet.name}` : mode === "milestone" ? (c.milestone || "No milestone") : (c.source || "Imported");
      return { c, already, owner, bucket };
    });
  }, [sheet, prep, mode]);
  const chosen = rows.filter((r) => !r.already && !skip.has(r.c.key));
  const slots = labelSlots(prep?.labels ?? {});

  async function send() {
    if (!plan || !chosen.length) return;
    setBusy("send"); setError("");
    try {
      const tasks = chosen.map(({ c, owner, bucket }) => ({
        key: c.key, title: c.title, bucket, start: c.start, due: c.due, percent: c.percent, priority: c.priority,
        assignee: owner?.id ?? "", labels: c.flags.map((f) => slots.map[f]).filter(Boolean),
        notes: owner || !c.owner ? c.notes : `Owner in the framework: ${c.owner} (not a member of this plan's group, so not assigned)\n${c.notes}`,
      }));
      const r = await graph<{ cancelled?: boolean; results?: { key: string; ok: boolean; error?: string }[] }>("planner.import", { planId: plan.id, planTitle: plan.title, tasks, labelNames: slots.names });
      if (r.cancelled) { setBusy(""); return; }
      const results = r.results ?? [];
      setDone({ ok: results.filter((x) => x.ok).length, failed: results.filter((x) => !x.ok) });
      toast(Tf("{n} tasks created in {plan}.", { n: results.filter((x) => x.ok).length, plan: plan.title }));
      onDone();
    } catch (e) { setError(cleanError(e)); } finally { setBusy(""); }
  }

  return (
    <div className="quick-back" onMouseDown={(e) => { if (e.target === e.currentTarget && busy !== "send") onClose(); }}>
      <div className="card quick s2p" role="dialog" aria-modal="true" aria-label={T("Send to Planner")}>
        <div className="row" style={{ gap: 10 }}><Orb size={26} mode={busy ? "think" : "breathe"} /><h2 style={{ fontSize: 19 }}>{T("Send to Planner")}</h2></div>

        {done ? (
          <div className="stack" style={{ gap: 10 }}>
            <p><b>{done.ok}</b> task{done.ok === 1 ? "" : "s"}{" "}{T("created in")}{" "}<b>{plan?.title}</b>.</p>
            {!!done.failed.length && <div className="notice"><span>{T("Not created:")}{" "}{done.failed.map((f) => `${f.key} (${f.error})`).join("; ")}</span></div>}
            <div className="row" style={{ justifyContent: "flex-end" }}><button className="btn primary" onClick={onClose}>{T("Done")}</button></div>
          </div>
        ) : (
          <>
            <div className="s2p-steps">
              <div className="fld"><span>{T("1. Workbook")}</span>
                <div className="row" style={{ gap: 8 }}>
                  <button className="btn" onClick={() => fileRef.current?.click()}><Icon name="clip" size={14} /> {fileName ? T("Choose another") : T("Choose Excel file")}</button>
                  {fileName && <span className="faint" style={{ fontSize: 12.5 }}>{fileName}</span>}
                </div>
                <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void pickFile(f); e.target.value = ""; }} />
              </div>
              {sheets && sheets.length > 1 && (
                <div className="fld"><span>{T("2. Sheet")}</span><div className="row" style={{ gap: 6 }}>{sheets.map((s, i) => <button key={s.name} className={`chip ${i === sheetIdx ? "on" : ""}`} onClick={() => setSheetIdx(i)}>{s.name} ({s.cards.length})</button>)}</div></div>
              )}
              <label className="fld"><span>{sheets && sheets.length > 1 ? "3" : "2"}{T(". Planner plan")}</span>
                <select value={newPlan ? "__new" : planId} onChange={(e) => void pickPlan(e.target.value)} disabled={!plans}>
                  <option value="">{!plans ? T("Loading your plans…") : plans.length ? T("Choose a plan") : T("No plans yet: create one below")}</option>
                  {plans?.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.group}</option>)}
                  {plans && <option value="__new">{T("+ Create a new plan…")}</option>}
                </select></label>
              {newPlan && (
                <div className="fld" style={{ gridColumn: "1 / -1" }}><span>{T("New plan")}</span>
                  <div className="row" style={{ gap: 8, flexWrap: "nowrap" }}>
                    <input value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder={T("VERA Operating")} aria-label={T("Plan name")} style={{ flex: 1 }} />
                    <select value={groupId} onChange={(e) => setGroupId(e.target.value)} aria-label={T("Team")} style={{ flex: 1 }}>
                      {!groups ? <option>{T("Loading your teams…")}</option> : !groups.length ? <option>{T("No teams found")}</option> : groups.map((g) => <option key={g.id} value={g.id}>{T("In:")}{" "}{g.name}</option>)}
                    </select>
                    <button className="btn primary" disabled={!groupId || !planName.trim() || busy === "create"} onClick={() => void createPlan()}>{busy === "create" ? T("Creating…") : T("Create plan")}</button>
                  </div>
                </div>
              )}
              {sheet && (
                <div className="fld"><span>{T("Buckets (Planner's columns)")}</span><div className="row" style={{ gap: 6 }}>
                  <button className={`chip ${mode === "source" ? "on" : ""}`} onClick={() => setMode("source")}>{sheet.kind === "actions" ? T("By Source / Forum") : T("By Workstream")}</button>
                  {sheet.kind === "actions" && <button className={`chip ${mode === "milestone" ? "on" : ""}`} onClick={() => setMode("milestone")}>{T("By Milestone")}</button>}
                  <button className={`chip ${mode === "single" ? "on" : ""}`} onClick={() => setMode("single")}>{T("One bucket")}</button>
                </div></div>
              )}
            </div>
            {plansError && <p style={{ color: "var(--bad)", fontSize: 13 }}>{plansError}</p>}
            {busy === "read" && <p className="faint">{T("Reading the workbook…")}</p>}
            {busy === "plan" && <p className="faint">{T("Checking the plan…")}</p>}

            {sheet && (
              <div className="s2p-preview">
                <table><thead><tr><th></th><th>{T("Card")}</th><th>{T("Bucket")}</th><th>{T("Assigned")}</th><th>{T("Dates")}</th><th>{T("Progress")}</th><th>{T("Priority")}</th></tr></thead>
                  <tbody>{rows.map(({ c, already, owner, bucket }) => (
                    <tr key={c.key} className={already || skip.has(c.key) ? "off" : ""}>
                      <td>{already ? <Pill tone="info">{T("Sent")}</Pill> : <input type="checkbox" aria-label={Tf("Send {what}", { what: c.key })} checked={!skip.has(c.key)} onChange={() => setSkip((s) => { const n = new Set(s); if (n.has(c.key)) n.delete(c.key); else n.add(c.key); return n; })} />}</td>
                      <td><b>{c.title}</b>{c.flags.map((f) => <Pill key={f} tone={f === "Blocked" ? "bad" : "warn"}>{f}</Pill>)}</td>
                      <td className="faint">{bucket}</td>
                      <td>{!c.owner ? <span className="faint">-</span> : !prep ? c.owner : owner ? owner.name : <span title={T("Not a member of this plan's group, so Planner can't assign them. Their name goes in the notes.")} style={{ color: "var(--warn)" }}>{c.owner}{" "}{T("(not in plan)")}</span>}</td>
                      <td className="faint" style={{ whiteSpace: "nowrap" }}>{[c.start, c.due].filter(Boolean).map((d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric" })).join(" → ") || "-"}</td>
                      <td>{PROGRESS_LABEL[c.percent]}</td>
                      <td>{c.priorityLabel}</td>
                    </tr>))}</tbody></table>
              </div>
            )}
            {sheet && <p className="faint" style={{ fontSize: 12 }}>{T("Everything else (support, dependencies, latest update, evidence, acceptance criteria) goes into each card's notes. Rows already in the plan show \"Sent\" and are skipped.")}</p>}
            {error && <p style={{ color: "var(--bad)", fontSize: 13 }} role="alert">{error}</p>}
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="faint" style={{ fontSize: 12 }}>{sheet && plan ? (chosen.length === 1 ? T("1 card to create. Windows asks you to confirm.") : Tf("{n} cards to create. Windows asks you to confirm.", { n: chosen.length })) : T("Choose the workbook and a plan to see the preview.")}</span>
              <div className="row" style={{ gap: 8 }}><button className="btn" onClick={onClose} disabled={busy === "send"}>{T("Cancel")}</button>
                <button className="btn primary" disabled={!plan || !prep || !chosen.length || !!busy} onClick={() => void send()}>{busy === "send" ? T("Creating cards…") : Tf("Send {n} to Planner", { n: chosen.length || "" })}</button></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
